import * as XLSX from "xlsx";
import { useCallback, useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogActions,
    Box, Grid, Typography, AppBar, Toolbar, IconButton,
    Alert,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    Chip,
    Stack,
    FormControl, Select, MenuItem
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from '@mui/icons-material/Add';
import { getIntlTranslation } from "../../../utils/helper";
import SaveButton from "../../../components/buttons/SaveButton";
import IntlMessages from "../../../utils/IntlMessages";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { createElementWithAI, uploadProtocol, createExistingElementWithAI } from "../api/maintainenceInspectionApi";
import FileDownloadButton from "../../../components/buttons/FileDownloadButton";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Button } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { parseDefectsString, toNumberedString } from "../utils/defectUtils";
import WarningIcon from '@mui/icons-material/WarningAmber';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const FMT = "YYYY-MM-DD";
const UPCOMING_WINDOW_MONTHS = 1;
const RECENT_WINDOW_DAYS = 30;

function AddReport({
    open,
    onClose,
    onSubmit,
    onDownloadTemplate,
    excel,
    report,
    protocol,
    siteId,
    elementList,
    addElement,
    onUploaded
}) {


    const [values, setValues] = useState({});
    const [excelRows, setExcelRows] = useState([])
    const [radioButtonValue, setRadioButtonValue] = useState('');
    const [reinspectionRequired, setReinspectionRequired] = useState(null);
    const intl = useIntl();
    const dispatch = useDispatch();
    const [selectedElement, setSelectedElement] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [defects, setDefects] = useState(() => parseDefectsString(values.defectsFound));
    const [newDefect, setNewDefect] = useState("");

    const parseD = (s) => (s ? dayjs(s, FMT, true) : null);

    useEffect(() => {
        setValues(v => ({ ...v, defectsFound: toNumberedString(defects) }));
    }, [defects, setValues]);

    useEffect(() => {
        setDefects(parseDefectsString(values.defectsFound));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        const computed = calcNextInspectionDate(values.lastInspectionDate, values.frequencyYears);
        if (computed !== values.nextInspectionDate) {
            setValues(v => ({ ...v, nextInspectionDate: computed, siteId: siteId }));
        }
    }, [values.lastInspectionDate, values.frequencyYears]);

    useEffect(() => {
        const s = getStatus({
            nextStr: values.nextInspectionDate,
            lastStr: values.lastInspectionDate,
            protocolUploaded: values.protocolUploaded ?? false,
        });
        if (s !== values.status) setValues(v => ({ ...v, status: s }));
    }, [values.nextInspectionDate, values.lastInspectionDate, values.protocolUploaded]);

    useEffect(() => {
        const canReinspect = values.status === "completed";
        if (!canReinspect) {
            // force “No” and clear any date
            if (reinspectionRequired) setReinspectionRequired(false);
            // setValues(v => ({ ...v, reinspectionRequired: '' }));
        }
        // if completed, do nothing — user may choose Yes and set a date
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.status]);

    const calcNextInspectionDate = (lastStr, freqYears) => {
        if (!lastStr || !freqYears || freqYears <= 0) return null;
        const last = dayjs(lastStr, "YYYY-MM-DD", true);
        if (!last.isValid()) return null;
        const next = last.add(freqYears, "year");
        return next.format("YYYY-MM-DD");
    };

    const addDefect = () => {
        const t = newDefect.trim();
        if (!t) return;
        const norm = (s) => s.toLowerCase().replace(/\s+/g, " ");
        if (defects.some(d => norm(d) === norm(t))) {
            setNewDefect("");
            return;
        }
        setDefects(prev => [...prev, t]);
        setNewDefect("");
    };

    const removeDefect = (idx) => setDefects(prev => prev.filter((_, i) => i !== idx));

    const getStatus = ({
        nextStr,
        lastStr,
        protocolUploaded,
    }) => {
        if (!nextStr) return "N/A";

        const today = dayjs().startOf("day");
        const next = dayjs(nextStr, FMT, true);
        if (!next.isValid()) return "N/A";

        // Completed / On Schedule logic
        if (protocolUploaded) {
            const last = parseD(lastStr);
            if (last?.isValid()) {
                const windowStart = next.subtract(RECENT_WINDOW_DAYS, "day");
                if (last.isSameOrAfter(windowStart, "day") && last.isSameOrBefore(today, "day")) {
                    return "completed";
                }
            }
        }

        // Overdue if today is past due
        if (today.isAfter(next, "day")) return "overdue";

        // Upcoming if due within the window
        const monthsUntilDue = next.diff(today, "month", true);
        if (monthsUntilDue <= UPCOMING_WINDOW_MONTHS) return "upcoming";

        // Otherwise, still fine
        return "completed";
    };

    const handleExcelUpload = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { raw: false });

            const newRows = worksheet.map((row) => {
                const defectsYesNo = (row["Defects (Yes/No)"] || "").toString().toLowerCase();
                const hasDefect = defectsYesNo === "yes";
                console.log('row,', row)
                return {
                    siteId: Number(siteId),
                    elementName: row["Element Name"] || row["elementName"],
                    lastInspectionDate: formatExcelDate(row["Last Inspection (DD.MM.YYYY)"] || row["Last Inspection Date"] || row["lastInspectionDate"]),
                    frequencyYears: Number(row["Frequency Count (In Years)"] || row["Frequency (Years)"] || row["frequencyYears"]),
                    nextInspectionDate: formatExcelDate(row["Next Inspection Date"]?.trim() || row["nextInspectionDate"]),
                    defectsFound: hasDefect ? (row["Defects Found"] || row["defectsFound"] || "") : "",
                    status: hasDefect ? "upcoming" : "completed",
                };
            });
            setExcelRows(newRows);
        };
        reader.readAsArrayBuffer(file);
    };

    // Helper to format Excel date to YYYY-MM-DD
    function formatExcelDate(dateValue) {
        if (!dateValue) return "";
        // If already in YYYY-MM-DD, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;
        // If in DD.MM.YYYY or DD-MM-YYYY or DD/MM/YYYY, convert
        const parts = dateValue.split(/[./-]/);
        if (parts.length === 3) {
            // If year is last (DD.MM.YYYY)
            if (parts[2].length === 4) {
                return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
            }
            // If year is first (YYYY.MM.DD)
            if (parts[0].length === 4) {
                return `${parts[0]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
            }
        }
        return dateValue;
    }

    const [fileError, setFileError] = useState("");
    const [aiError, setAiError] = useState("");
    const onFileSelected = (file) => {
        if (!file) {
            setValues((v) => ({ ...v, file: null }));
            setFileError("");
            return;
        }
        if (protocol && file.type !== "application/pdf" && !file.name.endsWith('.pdf')) {
            setFileError("Only PDF files are allowed for protocol upload.");
            setValues((v) => ({ ...v, file: null }));
            return;
        }
        if (excel && file.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" && !file.name.endsWith('.xlsx')) {
            setFileError("Only Excel (.xlsx) files are allowed for Excel upload.");
            setValues((v) => ({ ...v, file: null }));
            return;
        }
        setFileError("");
        setValues((v) => ({ ...v, file }));
        // If Excel file, handle bulk upload
        if (file && file.name && file.name.endsWith('.xlsx')) {
            handleExcelUpload(file);
        }
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const f = e.dataTransfer?.files?.[0];
        if (f) onFileSelected(f);
    }, [onFileSelected]);

    const handleBrowse = (e) => {
        const f = e.target.files?.[0];
        if (f) onFileSelected(f);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'frequencyYears')
            setValues((v) => ({ ...v, [name]: Number(value) }));
        else
            setValues((v) => ({ ...v, [name]: value }));
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        if (protocol) {
            if (!values.title || !values.version || !values.file) {
                setFileError("Please provide title, version, and select a PDF file.");
                setIsSubmitting(false);
                return;
            }
            if (!Number(siteId)) {
                setFileError("Missing inspection ID.");
                setIsSubmitting(false);
                return;
            }
            const formData = new FormData();
            formData.append("inspectionId", Number(siteId));
            formData.append("title", values.title);
            formData.append("version", values.version);
            formData.append("file", values.file);
            try {
                await dispatch(uploadProtocol({ data: formData }));
                onUploaded?.();
            } finally {
                onClose?.();
            }
        }
        if (report) {
            if (radioButtonValue === '') {
                setAiError("Please select an option to add a new element or use an existing one.");
            }
            if (!values.file) {
                setFileError("Please select a file to extract.");
                return;
            }
            if (!siteId) {
                setFileError("Missing siteId.");
                return;
            }
            if (radioButtonValue === 'newElement') {
                await dispatch(createElementWithAI({ siteId, file: values.file }));
                onUploaded?.()
            }
            else {
                console.log(selectedElement.id, 'selectedElement from dispatch')
                const inspectionId = selectedElement.id;
                await dispatch(createExistingElementWithAI({ siteId, file: values.file, inspectionId }));
                onUploaded?.()
            }
            if (typeof onClose === "function") onClose();
            return;
        }
        if (typeof onSubmit === "function") {
            if (excel) {
                onSubmit(excelRows);
            } else {
                onSubmit(values);
            }
        }
        if (typeof onClose === "function") onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            TransitionProps={{ unmountOnExit: true }}
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <AppBar
                className="appBar"
                position="relative"
                sx={{ bgcolor: "background.paper", color: "text.primary", }}
            >
                <Toolbar className="dialog-toolbar" sx={{ display: "flex", justifyContent: "space-between", }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, width: '100%' }}>
                        {protocol ? <IntlMessages id="mi.upload-protocol" /> : addElement ? <IntlMessages id="mi.add-element" /> : <IntlMessages id="mi.add-report" />}
                    </Typography>
                    {excel ? <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", mb: 2, alignSelf: "center", marginTop: 2 }}>
                        <FileDownloadButton
                            id="Download-Defect-Template"
                            endIcon={FileDownloadIcon}
                            size='medium'
                            className="g-btn-primary"
                            tooltip={{ title: getIntlTranslation("tooltip.download-excel-template") }}
                            handleClick={() => onDownloadTemplate()}
                            buttonText="mi.report-template"
                        />
                    </Box> : null}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                            edge="end"
                            color="inherit"
                            aria-label="close"
                            onClick={onClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>
            <DialogContent dividers sx={{ px: 3, pt: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        {report ? <Alert severity="warning" sx={{ mb: 2 }}>
                            {intl.formatMessage({ id: "mi.ai-warning" })}
                        </Alert> : null}
                        {addElement ? <Grid item xs={12} >
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                <IntlMessages id="mi.element-name" />
                            </Typography>
                            <TextField
                                fullWidth size="small"
                                placeholder={intl.formatMessage({ id: "mi.enter-element-name" })}
                                value={values.elementName}
                                name="elementName"
                                onChange={handleChange}
                            />
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                <IntlMessages id="mi.last-inspection" />
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    format="DD.MM.YYYY"
                                    value={
                                        values.lastInspectionDate
                                            ? dayjs(values.lastInspectionDate, "YYYY-MM-DD")
                                            : null
                                    }
                                    onChange={(newValue) => {
                                        setValues((v) => ({
                                            ...v,
                                            lastInspectionDate: newValue ? newValue.format("YYYY-MM-DD") : null,
                                        }));
                                    }}
                                    slotProps={{
                                        textField: {
                                            variant: "outlined",
                                            size: "small",
                                            helperText: "",
                                            error: false,
                                            fullWidth: true,
                                            sx: { mt: 1, width: "100%" },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                <IntlMessages id="mi.frequency" />

                            </Typography>
                            <TextField
                                fullWidth size="small"
                                placeholder={intl.formatMessage({ id: "mi.enter-frequency" })}
                                type="number"
                                value={values.frequencyYears}
                                name="frequencyYears"
                                onChange={handleChange}
                            />
                            <Box>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    <IntlMessages id="mi.next-inspection" />
                                </Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        readOnly
                                        format="DD.MM.YYYY"
                                        value={
                                            values.nextInspectionDate
                                                ? dayjs(values.nextInspectionDate, "YYYY-MM-DD")
                                                : null
                                        }
                                        slotProps={{
                                            textField: {
                                                variant: "outlined",
                                                size: "small",
                                                helperText: "",
                                                error: false,
                                                fullWidth: true,
                                                sx: { mt: 1, width: "100%" },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {getIntlTranslation("mi.defects-found")}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={intl.formatMessage({ id: "mi.enter-defect" })}
                                    value={newDefect}
                                    onChange={(e) => setNewDefect(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDefect(); } }}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={addDefect}
                                    sx={{
                                        borderColor: "#254a9a",
                                        color: "#254a9a",
                                        backgroundColor: "white",
                                        borderWidth: 1,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        fontWeight: 500,
                                        height: "40px",
                                        minWidth: "auto",
                                        px: 2,
                                        whiteSpace: "nowrap",
                                        '&:hover': {
                                            backgroundColor: '#e6ecfb',
                                            borderColor: '#254a9a',
                                        },
                                    }}
                                >
                                    {<IntlMessages id="mi.add-defect" />}
                                </Button>
                            </Stack>

                            <Stack direction="row" sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
                                {defects.length === 0 ? (
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>

                                    </Typography>
                                ) : (
                                    defects.map((d, i) => (
                                        <Chip
                                            key={`${d}-${i}`}
                                            label={`${i + 1}. ${d}`}
                                            onDelete={() => removeDefect(i)}
                                            deleteIcon={<CloseIcon />}
                                            variant="outlined"
                                            sx={{ mb: 1 }}
                                        />
                                    ))
                                )}
                            </Stack>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                <IntlMessages id="mi.status" />
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                                {values.status === "completed" && <Chip label={intl.formatMessage({ id: "mi.completed" })} color="success" size="small" />}
                                {values.status === "overdue" && <Chip label={intl.formatMessage({ id: "mi.overdue" })} color="error" size="small" />}
                                {values.status === "upcoming" && <Chip label={intl.formatMessage({ id: "mi.upcoming" })} color="warning" size="small" />}
                                {values.status === "N/A" && <Chip label={intl.formatMessage({ id: "mi.na" })} size="small" />}
                            </Box>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                <IntlMessages id="mi.reinspection-required" />
                            </Typography>
                            <RadioGroup
                                value={reinspectionRequired}
                                onChange={(event) => setReinspectionRequired(event.target.value)}
                                row
                            >
                                <FormControlLabel
                                    value="true"
                                    control={<Radio />}
                                    label={intl.formatMessage({ id: "mi.yes" })}
                                    disabled={values.status !== "completed"}
                                />
                                <FormControlLabel
                                    value="false"
                                    control={<Radio />}
                                    label={intl.formatMessage({ id: "mi.no" })}
                                    disabled={values.status !== "completed"}
                                />
                            </RadioGroup>
                            {reinspectionRequired === 'true' ? <Grid item xs={12} >
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    <IntlMessages id="mi.reinspection-date" />
                                </Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        format="DD.MM.YYYY"
                                        value={
                                            values.reinspectionRequired
                                                ? dayjs(values.reinspectionRequired, "DD.MM.YYYY")
                                                : null
                                        }
                                        onChange={(newValue) => {
                                            setValues((v) => ({
                                                ...v,
                                                reinspectionRequired: newValue ? newValue.format("DD.MM.YYYY") : null,
                                            }));
                                        }}
                                        slotProps={{
                                            textField: {
                                                variant: "outlined",
                                                size: "small",
                                                helperText: "",
                                                error: false,
                                                fullWidth: true,
                                                sx: { mt: 1, width: "100%" },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid> : null}
                        </Grid> : null}
                        {protocol ? <div >
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}  >
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        <IntlMessages id="user.title" />
                                    </Typography>
                                    <TextField
                                        fullWidth size="small"
                                        placeholder={intl.formatMessage({ id: "mi.enter-title" })}
                                        value={values.title}
                                        name="title"
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}  >
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        <IntlMessages id="mi.version" />
                                    </Typography>
                                    <TextField
                                        fullWidth size="small"
                                        placeholder={intl.formatMessage({ id: "mi.enter-version" })}
                                        value={values.version}
                                        name="version"
                                        onChange={handleChange}
                                    />
                                </Grid>
                            </Grid>
                        </div> : null}
                        {report ? (
                            <div>
                                <Grid item xs={12}>
                                    <FormControl component="fieldset">
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                            {getIntlTranslation("mi.select-option")}
                                        </Typography>
                                        <RadioGroup
                                            value={radioButtonValue}
                                            onChange={(event) => {
                                                setRadioButtonValue(event.target.value)
                                                setAiError('')
                                            }
                                            }
                                            row
                                        >
                                            <FormControlLabel value="newElement" control={<Radio />} label="New Element" />
                                            <FormControlLabel value="existingElement" control={<Radio />} label="Existing Element" />
                                        </RadioGroup>
                                        {aiError && (
                                            <>
                                                <Typography variant="caption" sx={{ color: "#aa3939" }}>
                                                    <WarningIcon sx={{ color: '#aa3939', fontSize: 20 }} />  {aiError}
                                                </Typography>
                                            </>
                                        )}
                                    </FormControl>
                                </Grid>
                                {radioButtonValue === 'existingElement' ? (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                            {getIntlTranslation("mi.select-element")}
                                        </Typography>
                                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                            <Select
                                                value={selectedElement}
                                                onChange={e => setSelectedElement(e.target.value)}
                                                displayEmpty
                                            >
                                                <MenuItem value="" disabled>{getIntlTranslation("mi-select-an-element")}</MenuItem>
                                                {elementList && elementList.length > 0 ? elementList.map((el) => (
                                                    <MenuItem key={el.id} value={el}>{el.elementName}</MenuItem>
                                                )) : null}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ) : null}
                            </div>
                        ) : null}

                        {!addElement ? <><Typography variant="caption" sx={{ color: "text.secondary" }}>
                            <IntlMessages id="mi.upload-file" />
                        </Typography>
                            <Box
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDrop={handleDrop}
                                sx={{
                                    mt: 1.5,
                                    p: 6,
                                    border: "2px dashed",
                                    borderColor: "divider",
                                    borderRadius: 3,
                                    bgcolor: (t) => t.palette.mode === "light" ? "#f9f9fb" : "background.default",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textAlign: "center",
                                    boxShadow: 2,
                                }}
                            >
                                <Box>
                                    <Typography sx={{ mb: 1.5 }}>
                                        {values.file
                                            ? `${intl.formatMessage({ id: "mi.selected" })} ${values.file.name}`
                                            : intl.formatMessage({ id: "mi.drag-file-here" })}
                                        {!values.file && (
                                            <>
                                                {" "}{intl.formatMessage({ id: "mi.or" })}{" "}
                                                <label
                                                    htmlFor="add-report-file"
                                                    style={{ color: "#254a9a", cursor: "pointer", textDecoration: "underline" }}
                                                >
                                                    {intl.formatMessage({ id: "mi.choose-file" })}
                                                </label>
                                                <input
                                                    id="add-report-file"
                                                    type="file"
                                                    style={{ display: "none" }}
                                                    onChange={handleBrowse}
                                                    accept={protocol ? ".pdf" : excel ? ".xlsx" : ".pdf"}
                                                />
                                            </>
                                        )}
                                    </Typography>
                                    {fileError && (
                                        <Alert severity="error" sx={{ mb: 1 }}>{fileError}</Alert>
                                    )}
                                    {values.file && (
                                        <Button
                                            variant="text"
                                            onClick={() => onFileSelected(null)}
                                            sx={{ textTransform: "none" }}
                                        >
                                            <IntlMessages id="mi.remove-file" />
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </> : null}
                        {excel && excelRows.length > 0 && (
                            <Box sx={{ mt: 3, height: 350, width: '100%' }}>
                                <DataGrid
                                    rows={excelRows.map((row, idx) => ({ id: idx + 1, ...row }))}
                                    columns={[
                                        { field: 'elementName', headerName: getIntlTranslation("mi.element-name"), flex: 1 },
                                        { field: 'lastInspectionDate', headerName: getIntlTranslation("mi.last-inspection"), flex: 1 },
                                        { field: 'frequencyYears', headerName: getIntlTranslation("mi.next-inspection"), flex: 1 },
                                        { field: 'nextInspectionDate', headerName: getIntlTranslation("mi.frequency"), flex: 1 },
                                        { field: 'defectsFound', headerName: getIntlTranslation("mi.defects-found"), flex: 1 },
                                        { field: 'status', headerName: getIntlTranslation("mi.status"), flex: 1 },
                                    ]}
                                    pageSize={5}
                                    rowsPerPageOptions={[5, 10, 20]}
                                    disableSelectionOnClick
                                />
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>

            {(!excel || (excel && excelRows.length > 0)) && (
                <DialogActions>
                    <SaveButton
                        id="add-report-button"
                        buttonText={excel ? "mi.add-excel-file" : report ? "mi.extract-data" : protocol ? "mi.upload-protocol" : addElement ? "mi.add-element" : "mi.add-report"}
                        variant="button"
                        size="medium"
                        style={{
                            margin: 5,
                            backgroundColor: "#d9534f",
                            color: "#ffffff",
                            height: 40,
                            padding: "0 16px",
                        }}
                        tooltip={{ title: getIntlTranslation("tooltip.add-site-details") }}
                        handleClick={handleSubmit}
                        disabled={isSubmitting}
                    />
                </DialogActions>
            )}
        </Dialog>
    );
}

export default AddReport